import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { StandardHotspot } from "./StandardHotspot";
import { getHotspotClassNames } from "../utils";
import {
  sanitizeUnsafeHTML,
  sanitizeConfigOptions,
} from "@labxchange/util-sanitization";
import { decodeHtmlEntities } from "../../utils/stringUtils";

import className from "../../style.module.scss";

export function DropdownHotspot(props) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const options = props.data?.props?.options ?? [];
  const currentIndex0 = Math.max(0, (props.data?.props?.currentState ?? 1) - 1);
  const currOpt = options[currentIndex0];

  const label = useMemo(() => {
    if (props.data.tooltipText) {
      return decodeHtmlEntities(
        sanitizeUnsafeHTML(
          intl.formatMessage({
            id: props.data.tooltipText,
            defaultMessage: props.data.tooltipText,
          }),
          sanitizeConfigOptions.PlainText
        )
      );
    }
    return intl.formatMessage({
      id: "Select an option",
      defaultMessage: "Select an option",
    });
  }, [intl, props.data.tooltipText]);

  const ariaLabel = useMemo(() => {
    const hotspotText = intl.formatMessage({
      id: "hotspot",
      defaultMessage: "hotspot",
    });
    return `${label} ${hotspotText}`;
  }, [intl, label]);

  const handleSelect = (newIndex0) => {
    const newState1 = newIndex0 + 1;
    const currentState1 = props.data?.props?.currentState ?? 1;

    if (newState1 !== currentState1) {
      props.changeState(props.id, newState1);
      if (!props.data.triggered && props.data.postTrigger !== "no-change") {
        props.handleTrigger(props.id);
      }
    }
  };

  const isDisabled =
    (props.data.triggered && props.data.postTrigger === "inactive") ||
    (props.locked && !props.data.triggered) ||
    (currOpt?.correctness === "Correct" &&
      props.data.triggered &&
      props.data.postTrigger !== "no-change");

  const isVisible =
    props.data.visibility_dependencies.views.includes(props.currentView) ||
    props.data.visibility_dependencies.views.length === 0;

  const containerStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    cursor: isDisabled ? "default" : "pointer",
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenu.Trigger asChild>
        <div
          id={`hotspotBtn_${props.id}`}
          className={getHotspotClassNames(props, className)}
          style={containerStyle}
          tabIndex={isVisible && !isDisabled ? 0 : -1}
          role="button"
          aria-disabled={isDisabled}
          aria-label={ariaLabel}
          disabled={isDisabled}
        >
          <StandardHotspot {...props} />
        </div>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={className.dropdownContent}
          align="center"
          sideOffset={5}
          role="menu"
        >
          <DropdownMenu.Arrow className="DropdownMenuArrow" />
          {options.map((opt, i) => {
            const isSelected = i === currentIndex0;
            return (
              <DropdownMenu.Item
                key={i}
                className={`${className.dropdownItem} ${
                  isSelected ? className.dropdownItemSelected : ""
                }`}
                onSelect={() => handleSelect(i)}
                disabled={isDisabled}
                role="menuitemradio"
                aria-checked={isSelected}
              >
                <span
                  className={`${className.radioIndicator} ${
                    isSelected ? className.radioIndicatorSelected : ""
                  }`}
                  aria-hidden="true"
                />
                {opt?.name ?? i + 1}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
